import { ApplicationRef, Component, DestroyRef, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { concat, filter, first, interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BUILD_VERSION } from '../environments/build-version';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrls: ['./app.css']
})
export class App {
  protected title = 'ten-charting';
  protected readonly hasUpdateAvailable = signal(false);
  protected readonly buildVersion = BUILD_VERSION;

  private readonly appRef = inject(ApplicationRef);
  private readonly updates = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    if (!this.updates.isEnabled) {
      return;
    }

    this.listenForVersionUpdates();
    this.scheduleUpdateChecks();
  }

  private listenForVersionUpdates(): void {
    this.updates.versionUpdates
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: VersionEvent) => {
        if (event.type === 'VERSION_READY') {
          this.hasUpdateAvailable.set(true);
          window.setTimeout(() => {
            void this.reloadToLatestVersion();
          }, 2500);
        }
      });
  }

  private scheduleUpdateChecks(): void {
    // Check once the app becomes stable, then every 6 hours.
    const appIsStable$ = this.appRef.isStable.pipe(
      first((isStable) => isStable)
    );

    concat(appIsStable$, interval(6 * 60 * 60 * 1000))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.updates.isEnabled)
      )
      .subscribe(() => {
        void this.updates.checkForUpdate();
      });
  }

  private async reloadToLatestVersion(): Promise<void> {
    try {
      await this.updates.activateUpdate();
      document.location.reload();
    } catch (error) {
      console.error('Failed to activate update', error);
    }
  }
}
